import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  preparePagination,
  prepareRequestFilters,
  prepareRequestSorters,
} from '@/utils/apiParamsUtils';

/**
 * Creates a new request with the provided data.
 * @param {Object} payload - The data for the new request.
 * @returns {Promise<Object>} A promise that resolves with the created request data.
 * @throws Will throw an error if the creation request fails.
 */
export const createNewRequest = async (payload) => {
  try {
    const response = await axiosClient.post('/requests/create', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create a new request:', error);
    throw error;
  }
};

/**
 * Retrieves all requests.
 * @returns {Promise<Object[]>} A promise that resolves with an array of request objects.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllRequests = async (params) => {
  const filters = prepareRequestFilters(params);
  const sorters = prepareRequestSorters(params);
  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/requests', {
      params: {
        filter: filters,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve requests:', error);
    throw error;
  }
};

/**
 * Updates an existing request by their ID with the provided data.
 * @param {string} requestId - The unique identifier of the request.
 * @param {Object} payload - The updated data for the employee.
 * @returns {Promise<Object>} A promise that resolves with the updated employee data.
 * @throws Will throw an error if the update request fails.
 */
export const updateRequestById = async (requestId, payload) => {
  try {
    const response = await axiosClient.put(`/requests/${requestId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Failed to update request with ID ${requestId}:`, error);
    throw error;
  }
};

/**
 * Sends a filled-in travel order to the people who approve it.
 * @param {string} requestId - The travel order.
 * @returns {Promise<{message: string, travelSubmission: Object}>}
 */
export const submitOfficialTravel = async (requestId) => {
  try {
    const response = await axiosClient.post(
      `/requests/${requestId}/official-travel/submit`
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to submit travel order ${requestId}:`, error);
    throw error;
  }
};

/**
 * Reads the receipts on a travel order's attachments. Nothing is saved; the
 * rows come back for review.
 * @param {string} requestId - The travel order.
 * @param {Array<string>} [fileIds] - Attachments to read; all when omitted.
 * @returns {Promise<{receipts: Array<Object>, failedFiles: Array<string>}>}
 */
export const readOfficialTravelReceipts = async (requestId, fileIds) => {
  try {
    const response = await axiosClient.post(
      `/requests/${requestId}/official-travel/read-receipts`,
      { fileIds },
      // Reading a scanned bundle takes a while.
      { timeout: 300000 }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to read receipts of request ${requestId}:`, error);
    throw error;
  }
};

/**
 * Journal lines booking a travel order in AB Soft, for review.
 * @param {string} requestId - The travel order.
 * @param {Object} [accounts] - Edited accounts to preview; saved ones when omitted.
 */
export const previewTravelJournal = async (requestId, accounts) => {
  try {
    const response = await axiosClient.post(
      `/requests/${requestId}/official-travel/ab-journal/preview`,
      accounts ? { accounts } : {}
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to preview the journal of request ${requestId}:`, error);
    throw error;
  }
};

/**
 * NALOG.xls booking an approved travel order in AB Soft.
 * @param {string} requestId - The travel order.
 * @param {{accounts: Object, folder: string, date: string}} payload
 * @returns {Promise<Blob>}
 */
export const exportTravelJournal = async (requestId, payload) => {
  try {
    const response = await axiosClient.post(
      `/requests/${requestId}/official-travel/ab-journal`,
      payload,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    // With a blob response the error body is a blob too; read it so the
    // message reaches the page.
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        /* keep the original error */
      }
    }
    console.error(`Failed to export the journal of request ${requestId}:`, error);
    throw error;
  }
};

/**
 * Retrieves a request by its ID.
 * @param {string} requestId - The unique identifier of the request.
 * @returns {Promise<Object>} A promise that resolves with the request data.
 * @throws Will throw an error if the request fails.
 */
export const getRequestById = async (requestId) => {
  try {
    const response = await axiosClient.get(`/requests/${requestId}`);
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve request with ID ${requestId}:`, error);
    throw error;
  }
};

/**
 * Deletes a request by its ID, with optional deletion of related dependencies.
 *
 * @param {string} requestId - The unique identifier of the request to be deleted.
 * @param {boolean} isWithDependenciesDelete - If true, related dependent data will also be deleted.
 * @returns {Promise<Object>} A promise that resolves with the response data upon successful deletion.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteRequestById = async (
  requestId,
  isWithDependenciesDelete
) => {
  try {
    return await axiosClient.delete(`/requests/${requestId}`, {
      params: isWithDependenciesDelete ? { withDependencies: true } : {},
    });
  } catch (error) {
    console.error(`Failed to delete request with ID ${requestId}:`, error);
    throw error;
  }
};

/**
 * Retrieves used dates based on the provided payload.
 * @param {Object} payload - The request payload containing filter parameters.
 * @returns {Promise<Object>} A promise that resolves with the used dates data.
 * @throws Will throw an error if the request fails.
 */
export const getUsedDates = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/requests/get-used-dates',
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve requests:', error);
    throw error;
  }
};

/**
 * Computes the number of leave days a date range consumes for an employee based
 * on their timesheet rota (rostered work days). Used to show the correct count
 * for shift workers, who consume only the days they are actually scheduled to
 * work rather than every calendar day.
 *
 * @param {Object} payload - { employeeId, startDate, endDate }.
 * @returns {Promise<{scheduled:number,total:number,hasRota:boolean}>}
 */
export const computeLeaveDays = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/requests/compute-leave-days',
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Failed to compute leave days:', error);
    throw error;
  }
};

const RequestsService = {
  createNewRequest,
  retrieveAllRequests,
  updateRequestById,
  readOfficialTravelReceipts,
  previewTravelJournal,
  exportTravelJournal,
  getRequestById,
  deleteRequestById,
  getUsedDates,
  computeLeaveDays,
};

export default RequestsService;
